import React, { useEffect, useState } from "react"
import {
    Filters,
    IItem as PFIItem,
    ItemPicker,
    Options,
    SortingOption,
} from "@tenforce/product-fields"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { IItemPickerField, ITextField } from "../../.."
import { useAppDispatch, useAppSelector } from "../../../../../hooks"
import { GlobalState } from "../../../../../store"
import HTMLAndInternalLinkParser from "../../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../../util/Translations"
import { addErrorNotification } from "../../../../notifications/actions"
import { getItemDetailUrl } from "../../../../../util/UrlGenerator"
import env from "../../../../../env"
import {
    fetchAvailableSortingFields,
    fetchCollectionItems as fetchCollectionItemsThunk,
    fetchConceptFields,
    getCollectionQuery,
    updateCollectionQuery,
} from "../../../../collection/slice"
import { FetchCollectionParams } from "../../../../collection"
import { Field } from "../../Field"
import { IItem } from "../../../../../models"
import { formatString } from "../../../../../util/Strings"
import { updateField } from "../../../slice"
import { getItemAncestry } from "../../../../../apis/items"
import { TitleCode } from "../../../../../apis/utils/constants"
import { useHierarchicalSelectableData } from "../../../../../hooks/useHierarchicalSelectableData"
import {
    FieldDefinitionTypeToSelectableDTOTypeMap,
    getSelectableConditionType,
} from "../../../../../apis/utils/conditions/SelectorConditionParser"
import { SelectableValue } from "../../../../../apis/treeSelector/types"

type Props = IItemPickerField

// TODO: These are just to trick tailwind into generating the dynamically created classes from the product-fields.
// It should at some point be removed once the SCSS is generated properly.
const _levelOne = "tw-ml-0"
const _levelTwo = "tw-ml-4"
const _levelThree = "tw-ml-8"
const _levelFour = "tw-ml-12"
const _levelFive = "tw-ml-16"

type SortingLevel = 0 | 1 | 2 | null

const PAGE_SIZE = 20

const convertNewUIItemToPFItem = (item: IItem): PFIItem => {
    return {
        id: item.id,
        number: item.number ?? "",
        title: item.title ?? "",
        url: getItemDetailUrl(env.applicationPath, "", item.id),
        parents: item.parents,
        fields: Object.values(item.fields ?? {}),
    }
}

const ItemPickerNew: React.FC<Props> = (props: Props) => {
    const dispatch = useAppDispatch()
    const [itemsWithParents, setItemsWithParents] = useState<IItem[]>([])
    const [isLoadingParents, setIsLoadingParents] = useState<boolean>(false)
    const [locallySelectedItems, setLocallySelectedItems] = useState<
        typeof items
    >([])
    const [isSortingLoading, setIsSortingLoading] = useState<boolean>(false)
    const [isFieldSelectionLoading, setIsFieldSelectionLoading] =
        useState<boolean>(true)

    const {
        description,
        fieldId,
        isDisabled,
        isLoading = false,
        isSingleSelect,
        itemId,
        itemType,
        odataType,
        title,
        value: initialValue,
        ...rest
    } = props

    const { isLoading: isLoadingGlobal, currentFieldInEditMode = "" } =
        useAppSelector((state: GlobalState) => state.fields)

    const {
        availableSortingOptions,
        filters,
        isQueryProcessing,
        items = [],
        selectableFieldsData,
        selectedSorting = [],
        total,
        visibleFields = [],
        visibleFieldsIds,
    } = useAppSelector((state: GlobalState) => state.collections[fieldId] || {})
    const [localSelectedSorting, setLocalSelectedSorting] =
        useState<SortingOption[]>(selectedSorting)
    const [activeSorting, setActiveSorting] = useState<SortingLevel>(null)

    // Relation filter ====================

    const { fetchOptions, fetchSelectedOptions } =
        useHierarchicalSelectableData({ pageSize: PAGE_SIZE, itemId, fieldId })

    // =====================================

    const fetchAncestry = async (itemId: number): Promise<IItem[]> => {
        try {
            let parents = await getItemAncestry(itemId)
            if (parents.length > 0) {
                parents = parents.map((parent) => ({
                    ...parent,
                    url: getItemDetailUrl(env.applicationPath, "", parent.id),
                }))
            }
            return parents
        } catch (error) {
            dispatch(
                addErrorNotification(
                    error instanceof Error
                        ? error.message
                        : "Something went wrong when fetching ancestry.",
                ),
            )
            return []
        }
    }

    useEffect(() => {
        const fetchParentsForItems = async (): Promise<void> => {
            const updatedItems = []
            for (const item of initialValue) {
                const parentsData = await fetchAncestry(item.id)
                updatedItems.push({
                    ...item,
                    parents: parentsData.filter(
                        (item) => item.id === 0 || !!item.id,
                    ),
                })
            }
            setItemsWithParents(updatedItems)
        }
        setIsLoadingParents(true)
        fetchParentsForItems().finally(() => setIsLoadingParents(false))
    }, [initialValue])

    const isFieldDisabled =
        isDisabled || !["", fieldId].includes(currentFieldInEditMode)

    const fetchCollectionData = async (): Promise<void> => {
        await dispatch(getCollectionQuery({ itemId, fieldId, odataType }))
    }

    const fetchCollectionItems = async (
        skip: number,
        limit: number,
    ): Promise<void> => {
        await dispatch(
            fetchCollectionItemsThunk({
                itemId,
                fieldId,
                odataType,
                skip: skip,
                top: limit,
            }),
        )
    }

    const skipValueIds = (activeLevel: SortingLevel): string[] => {
        if (activeLevel === 1) {
            if (localSelectedSorting && localSelectedSorting.length > 0) {
                return localSelectedSorting[0].columnId
                    ? [localSelectedSorting[0].columnId]
                    : []
            }
            return []
        }

        if (activeLevel === 2) {
            const ids: string[] = []
            if (localSelectedSorting && localSelectedSorting.length > 0) {
                if (localSelectedSorting[0].columnId) {
                    ids.push(localSelectedSorting[0].columnId)
                }
                if (localSelectedSorting[1]?.columnId) {
                    ids.push(localSelectedSorting[1].columnId)
                }
            }
            return ids
        }

        return []
    }

    const fetchSortingFields = async (
        activeLevel: SortingLevel,
    ): Promise<void> => {
        setIsSortingLoading(true)
        setActiveSorting(activeLevel)
        const localSelectedId = skipValueIds(activeLevel)

        await dispatch(
            fetchAvailableSortingFields({
                itemId,
                fieldId,
                odataType,
                filter: "",
                skip: 0,
                top: PAGE_SIZE,
                localSelectedSortingId: localSelectedId,
            }),
        )
        setIsSortingLoading(false)
    }

    const fetchSelectableFields = async (): Promise<void> => {
        setIsFieldSelectionLoading(true)
        await dispatch(
            fetchConceptFields({
                itemId,
                fieldId,
                odataType,
                filter: "",
                top: PAGE_SIZE,
                skip: 0,
            }),
        )
        setIsFieldSelectionLoading(false)
    }

    const onSaveViewSettings = async (settingsData: {
        selectedFields?: string[]
        sorting?: SortingOption[]
    }): Promise<void> => {
        const apiPayload: FetchCollectionParams = {
            itemId,
            fieldId,
            odataType,
            selectedFields: settingsData.selectedFields
                ? settingsData.selectedFields
                : visibleFieldsIds,
            sorting:
                settingsData?.sorting?.map(
                    (option: SortingOption): SortingOption => {
                        return {
                            mode: option.mode,
                            columnId: option.columnId,
                            label: option.label,
                        }
                    },
                ) ?? [],
        }

        await dispatch(updateCollectionQuery(apiPayload)).unwrap()
        if (settingsData.sorting) {
            setLocalSelectedSorting(settingsData.sorting)
        }
        setActiveSorting(0)
        await fetchCollectionData()
        await fetchCollectionItems(0, PAGE_SIZE)
    }

    const onFilterChange = (filter: string): void => {
        dispatch(
            fetchConceptFields({
                itemId,
                fieldId,
                odataType,
                filter,
                top: PAGE_SIZE,
                skip: 0,
            }),
        )
    }

    const onLoadMoreSelectableFields = (filter: string): void => {
        dispatch(
            fetchConceptFields({
                itemId,
                fieldId,
                odataType,
                filter,
                top: PAGE_SIZE,
                skip: selectableFieldsData.currentSkip,
            }),
        )
    }

    const onSave = async (itemIds: number[]): Promise<void> => {
        await dispatch(
            updateField({
                field: props,
                newValue: itemIds,
            }),
        ).unwrap()
    }

    const onSortingFilterChange = async (filter: string): Promise<void> => {
        setIsSortingLoading(true)
        await dispatch(
            fetchAvailableSortingFields({
                itemId,
                fieldId,
                odataType,
                filter,
                skip: 0,
                top: PAGE_SIZE,
                localSelectedSortingId: skipValueIds(activeSorting),
            }),
        ).unwrap()
        setIsSortingLoading(false)
    }

    const onSortingLoadMore = (filter: string): void => {
        setIsSortingLoading(true)
        const nextSkip = availableSortingOptions?.currentSkip || 0
        dispatch(
            fetchAvailableSortingFields({
                itemId,
                fieldId,
                odataType,
                filter,
                skip: nextSkip,
                top: PAGE_SIZE,
                localSelectedSortingId: skipValueIds(activeSorting),
            }),
        )
        setIsSortingLoading(false)
    }

    const onCollectionFiltersChange = async (
        filters: Filters,
    ): Promise<void> => {
        const args: FetchCollectionParams = {
            itemId,
            fieldId,
            odataType,
            filters,
        }

        await dispatch(updateCollectionQuery(args)).unwrap()
        await fetchCollectionData()
        await fetchCollectionItems(0, PAGE_SIZE)
    }

    const {
        ClickHereToEdit,
        Columns,
        FilterAllNValues,
        FilterNSelectedValues,
        FilterOnX,
        ItemOverviewSorting,
        ItemPickerAddItem,
        ItemPickerAddItems,
        ItemPickerEditItem,
        ItemPickerEditItems,
        ItemboxFilter,
        LoadNMore,
        LoadNMoreOf,
        NothingFound,
        OpenViewSettings,
        ShowAllValues,
        ShowSelectedValues,
        SortLevelOne,
        SortLevelThree,
        SortLevelTwo,
        ViewSettings,
        EmptyValue,
        RemoveEmptyValue,
    } = Labels

    const regionalDecimalSeparator = useAppSelector(
        (state: GlobalState) =>
            state.userFormattingSettings.numberFormat.decimalSeparator,
    )

    // Relation filter ======================

    const fetchSelectableFilterOptions = async (
        filter: string,
        activeFieldId: string,
        type: string,
        skip: number,
    ): Promise<Options<SelectableValue>> => {
        const oDataType =
            type as keyof typeof FieldDefinitionTypeToSelectableDTOTypeMap
        const selectableApiType = getSelectableConditionType(oDataType)
        const result = await fetchOptions(
            filter,
            activeFieldId,
            selectableApiType,
            skip,
            [],
        )
        return result
    }

    const fetchSelectedFilterOptions = async (
        filterValue: string,
        selectedIdsParam: string[],
        _fieldId: string,
        type: string,
    ): Promise<
        Omit<Options<SelectableValue>, "resultCount" | "isGroupAsValue">
    > => {
        const oDataType =
            type as keyof typeof FieldDefinitionTypeToSelectableDTOTypeMap
        const selectableApiType = getSelectableConditionType(oDataType)

        const result = await fetchSelectedOptions(
            filterValue,
            selectedIdsParam,
            selectableApiType,
        )
        return result
    }
    // =====================================

    return (
        <ItemPicker
            {...rest}
            fieldId={fieldId}
            title={title}
            isDisabled={isFieldDisabled}
            isLoading={
                isLoadingGlobal ||
                isLoading ||
                isQueryProcessing ||
                isLoadingParents
            }
            isSingleSelect={isSingleSelect}
            value={itemsWithParents.map(convertNewUIItemToPFItem)}
            labels={{
                AddItem: ItemPickerAddItem,
                AddItems: ItemPickerAddItems,
                EditItem: ItemPickerEditItem,
                EditItems: ItemPickerEditItems,
                EmptyPlaceholder: ClickHereToEdit,
                FieldSelectionSectionLabel: Columns,
                FilterAllNValues: (count) =>
                    Symbols.Search + formatString(FilterAllNValues, count),
                FilterOnX: (fieldName) =>
                    formatString(FilterOnX, fieldName.toLowerCase()),
                FilterNSelectedValues: (count) =>
                    Symbols.Search + formatString(FilterNSelectedValues, count),
                FilterNoResults: NothingFound,
                LoadNMore: (remaining) => formatString(LoadNMore, remaining),
                LoadNMoreOf: (pageSize, remaining) =>
                    formatString(LoadNMoreOf, pageSize, remaining),
                OpenViewSettings: OpenViewSettings,
                ShowAllValuesTooltip: ShowAllValues,
                ShowSelectedValuesTooltip: ShowSelectedValues,
                SortLevelOne: SortLevelOne,
                SortLevelThree: SortLevelThree,
                SortLevelTwo: SortLevelTwo,
                SortingSectionLabel: ItemOverviewSorting,
                ToggleFilter: ItemboxFilter,
                ViewSettings: ViewSettings,
                EmptyValue: EmptyValue,
                RemoveEmptyValue: RemoveEmptyValue,
            }}
            onSave={onSave}
            description={
                <HTMLAndInternalLinkParser
                    content={(description as string) ?? ""}
                />
            }
            collection={{
                fetchCollectionData: fetchCollectionData,
                getItemField: (itemField): JSX.Element => {
                    return (
                        <Field
                            field={{ ...itemField, itemType }}
                            isLoading={false}
                        />
                    )
                },
                onSaveViewSettings: onSaveViewSettings,
                visibleFields: visibleFields,
                fieldSelection: {
                    isLoading: isFieldSelectionLoading,
                    onFilterChange: onFilterChange,
                    onLoadMore: onLoadMoreSelectableFields,
                    selectableFields: {
                        data: selectableFieldsData?.allConceptFields || [],
                        totalCount: selectableFieldsData?.totalValueCount || 0,
                        currentSkip: selectableFieldsData?.currentSkip || 0,
                        hasMore: selectableFieldsData?.hasMore || false,
                    },
                    fetchOptions: fetchSelectableFields,
                },
                sorting: {
                    isLoading: isSortingLoading,
                    fetchSelectable: fetchSortingFields,
                    selected: selectedSorting.map((sorting) => {
                        return {
                            columnId: sorting.columnId,
                            mode: sorting.mode,
                            label: sorting.label,
                        }
                    }),
                    localSelected: localSelectedSorting,
                    selectableOptions: {
                        data: availableSortingOptions?.selectableOptions || [],
                        hasMore: availableSortingOptions?.hasMore || false,
                        totalCount:
                            availableSortingOptions?.totalValueCount || 0,
                        currentSkip: availableSortingOptions?.currentSkip || 0,
                    },
                    onFilterChange: onSortingFilterChange,
                    onLoadMore: onSortingLoadMore,
                    onChange: (sorting: SortingOption[]): void =>
                        setLocalSelectedSorting(sorting),
                },
                filtering: {
                    filters: filters,
                    regionalDecimalSeparator: regionalDecimalSeparator,
                    onSaveFilters: onCollectionFiltersChange,
                    filterSelectorProps: {
                        fetchSelectableOptions: fetchSelectableFilterOptions,
                        fetchSelectedOptions: fetchSelectedFilterOptions,
                    },
                },
                selectableView: {
                    options: items.map(convertNewUIItemToPFItem),
                    totalCount: total,
                    fetchOptions: async (
                        _filter,
                        skip,
                        limit,
                    ): Promise<void> => {
                        return fetchCollectionItems(skip, limit)
                    },
                },
                selectedView: {
                    options: locallySelectedItems.map(convertNewUIItemToPFItem),
                    totalCount: locallySelectedItems.length,
                    fetchOptions: async (_filter, ids): Promise<void> => {
                        const newSelectedItems = ids.reduce((acc, id) => {
                            const selected: IItem | undefined =
                                locallySelectedItems.find(
                                    (item) => item.id.toString() === id,
                                )
                            if (selected) {
                                return [...acc, selected]
                            }
                            const selectable: IItem | undefined = items.find(
                                (item) => item.id.toString() === id,
                            )
                            if (selectable) {
                                return [...acc, selectable]
                            }
                            let initial: IItem | undefined = initialValue.find(
                                (item) => item.id.toString() === id,
                            )
                            if (initial) {
                                initial = {
                                    ...initial,
                                    fields:
                                        // The values we get from the backend for selected values
                                        // do not contain fields but just properties (id, title, ...).
                                        // Therefore, we are cheating here a little to provide the product-fields
                                        // with an array of fields it can render for the selected view.
                                        Object.values(initial.fields ?? {})
                                            .length > 0
                                            ? initial.fields
                                            : {
                                                  [TitleCode]: {
                                                      description: "",
                                                      fieldId: TitleCode,
                                                      isAutoCompleteEnabled:
                                                          false,
                                                      isDisabled: false,
                                                      isReadOnly: true,
                                                      isRequired: false,
                                                      itemId: itemId,
                                                      itemType: itemType,
                                                      maxLength: 80,
                                                      odataType:
                                                          "#TF.TextItemField",
                                                      position: { x: 0, y: 0 },
                                                      title: "",
                                                      type: "smallText",
                                                      value: initial.title,
                                                  } as ITextField,
                                              },
                                }
                                return [...acc, initial]
                            }
                            return acc
                        }, [])
                        setLocallySelectedItems(newSelectedItems)
                    },
                },
            }}
        />
    )
}

export default ItemPickerNew
